import { type ClipboardEvent, type KeyboardEvent, useEffect, useRef, useState } from 'react';

import { Input } from '@heroui/react';

type CodeInputProps = {
  length?: number;
  onComplete: (code: string) => void;
  disabled?: boolean;
  error?: boolean;
  onChange?: (code: string) => void;
};

export const CodeInput = ({
  length = 6,
  onComplete,
  disabled = false,
  error = false,
  onChange,
}: CodeInputProps) => {
  const [codes, setCodes] = useState<string[]>(new Array(length).fill(''));
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const fullCode = codes.join('');
    if (fullCode.length === length && fullCode !== '' && !disabled && !hasSubmitted) {
      setHasSubmitted(true);
      onComplete(fullCode);
    }
  }, [codes, length, onComplete, disabled, hasSubmitted]);

  const focusInput = (index: number) => {
    const nextInput = inputRefs.current[index];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    }
  };

  const handleChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;

    const digit = value.slice(-1);
    const newCodes = [...codes];
    newCodes[index] = digit;
    setCodes(newCodes);

    // Reset submission flag when user modifies code
    if (hasSubmitted) {
      setHasSubmitted(false);
    }

    // Call onChange callback if provided
    if (onChange) {
      onChange(newCodes.join(''));
    }

    if (digit && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      focusInput(index - 1);
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusInput(index - 1);
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '').slice(0, length);

    if (digits.length > 0) {
      const newCodes = new Array(length).fill('');
      for (let i = 0; i < digits.length && i < length; i++) {
        newCodes[i] = digits[i];
      }
      setCodes(newCodes);

      // Reset submission flag when user pastes code
      if (hasSubmitted) {
        setHasSubmitted(false);
      }

      // Call onChange callback if provided
      if (onChange) {
        onChange(newCodes.join(''));
      }

      // Focus the next empty input or the last input
      const nextFocusIndex = Math.min(digits.length, length - 1);
      focusInput(nextFocusIndex);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-1 sm:gap-1.5 md:gap-2 justify-center">
        {codes.map((code, index) => (
          <Input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            value={code}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            disabled={disabled}
            maxLength={1}
            className="w-7 h-7 sm:w-10 sm:h-10 md:w-12 md:h-14"
            classNames={{
              input: [
                'text-center',
                'text-gray-800',
                'text-xs',
                'sm:text-base',
                'md:text-lg',
                'lg:text-xl',
                'font-bold',
                'p-0',
                'h-full',
                'flex',
                'items-center',
                'justify-center',
                'leading-none',
              ],
              inputWrapper: [
                'bg-white/80',
                'backdrop-blur-sm',
                'border-gray-200',
                'hover:border-gray-300',
                'rounded-xl',
                'h-7',
                'sm:h-10',
                'md:h-12',
                'lg:h-14',
                'w-7',
                'sm:w-10',
                'md:w-12',
                'lg:w-14',
                'min-h-0',
                'p-0',
                'shadow-sm',
                'hover:shadow-md',
                'transition-all',
                'duration-200',
                error ? 'border-red-500 hover:border-red-400' : 'focus-within:border-violet-400',
              ],
              base: [
                'h-7',
                'sm:h-10',
                'md:h-12',
                'lg:h-14',
                'w-7',
                'sm:w-10',
                'md:w-12',
                'lg:w-14',
                'min-h-0',
                'p-0',
              ],
              mainWrapper: ['h-full', 'w-full', 'p-0', 'flex', 'items-center', 'justify-center'],
            }}
            type="text"
            inputMode="numeric"
          />
        ))}
      </div>

      {error && (
        <p className="text-red-500 text-xs sm:text-sm text-center">
          Invalid verification code. Please try again.
        </p>
      )}
    </div>
  );
};
